import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams, Link, useLocation } from 'react-router-dom';
import { AuthContext } from '../App';
import { trackEvent } from '../utils/analytics';
import { captureLeadEmail } from '../utils/captureLeadEmail';

function ApplicationForm() {
  const { user, authReady } = useContext(AuthContext);
  const { propertyId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();


  const consentVersion = 'consumer_report_v2';
  const consentText = `I authorize Rentual Corp. to obtain my consumer report and related identity/credit-risk information for the purpose of evaluating my rental application and for fraud prevention. I understand that my submitted documents and personal information may be processed using automated tools, including third-party artificial intelligence services, to assist in the evaluation of my application. This processing is limited to screening purposes and my data will not be used for AI model training.`;



  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phoneNumber: '',
    currentAddress: '',
    currentEmployer: '',
    employmentStatus: '',
    income: '',
    numOccupants: 1,
    petInfo: '',
    moveInDate: '',
    consentBackgroundCheck: false,
    message: '', // New unified message field
    consentData: false, // New consent field
    consentSignature: ''
  });
  const [prefillSources, setPrefillSources] = useState({});
  const [property, setProperty] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);



  useEffect(() => {
    const redirectTarget = `${location.pathname}${location.search}`;
    if (!authReady) return;
    if (!user || user.userType !== 'tenant') {
      navigate(`/register?redirect=${encodeURIComponent(redirectTarget)}`);
      return;
    }
  }, [authReady, user, navigate, location.pathname, location.search]);

  useEffect(() => {
    const fetchPropertyDetails = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/properties/${propertyId}`);
        if (!response.ok) throw new Error('Could not fetch property details');
        const data = await response.json();
        setProperty(data.property);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchPropertyDetails();
  }, [propertyId]);

  useEffect(() => {
    const prefillFromProfile = async () => {
      if (!user?.token || user.userType !== 'tenant') return;
      try {
        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/tenants/profile`, {
          headers: { 'Authorization': `Bearer ${user.token}` }
        });
        if (!res.ok) return;
        const data = await res.json().catch(() => ({}));
        const profile = data.profile || {};
        const fullAddress = [profile.address_line1, profile.city, profile.province, profile.postal_code, profile.country]
          .filter(Boolean)
          .join(', ');
        const derivedEmail = user?.email || profile.email || '';
        const derivedName = [profile.first_name, profile.last_name].filter(Boolean).join(' ');
        const derivedPhone = profile.phone || '';
        const derivedAddress = fullAddress || profile.address_line1 || '';

        setFormData(prev => ({
          ...prev,
          fullName: prev.fullName || derivedName,
          email: prev.email || derivedEmail,
          phoneNumber: prev.phoneNumber || derivedPhone,
          currentAddress: prev.currentAddress || derivedAddress
        }));

        setPrefillSources(prev => ({
          ...prev,
          ...(derivedEmail && !prev.email ? { email: 'profile' } : {}),
          ...(derivedName && !prev.fullName ? { fullName: 'profile' } : {}),
          ...(derivedPhone && !prev.phoneNumber ? { phoneNumber: 'profile' } : {}),
          ...(derivedAddress && !prev.currentAddress ? { currentAddress: 'profile' } : {})
        }));
      } catch (err) {
        // silent
      }
    };

    prefillFromProfile();
  }, [user, propertyId]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleEmailBlur = () => {
    captureLeadEmail(formData.email, window.location.pathname, { context: 'application_form', property_id: propertyId });
  };

  const renderPrefillNote = (field) => {
    if (prefillSources[field] === 'profile') {
      return <p className="subtle-text" style={{ marginTop: '0.35rem' }}>From your profile - please confirm accuracy.</p>;
    }
    return null;
  };

  const isValidPhone = (value) => {
    if (!value) return true;
    const digits = value.replace(/\D/g, '');
    return digits.length >= 10 && digits.length <= 15;
  };

  const isValidAddress = (value) => {
    if (!value) return true;
    return value.trim().length >= 5;
  };

  const getVerificationBadge = () => {
    if (!property) return { label: 'Unverified landlord', tone: 'warning' };
    if (property.landlord_ownership_status === 'approved' || property.landlord_id_status === 'verified') {
      return { label: 'Verified landlord', tone: 'paid' };
    }
    return { label: 'Unverified landlord', tone: 'warning' };
  };

  const canSubmitApplication = property?.landlord_ownership_status === 'approved' || property?.landlord_id_status === 'verified';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!user || user.userType !== 'tenant') {
      alert('You must be logged in as a tenant to submit an application.');
      const redirectTarget = `${location.pathname}${location.search}`;
      navigate(`/register?redirect=${encodeURIComponent(redirectTarget)}`);
      return;
    }



    if (!property) {
      setError('Property details not loaded. Cannot submit.');
      return;
    }

    if (property.landlord_ownership_status !== 'approved' && property.landlord_id_status !== 'verified') {
      setError('Applications are disabled until the landlord is verified.');
      return;
    }

    if (!formData.consentData) {
      setError('You must consent to the consumer report authorization to proceed.');
      return;
    }

    if (!formData.consentSignature || !formData.consentSignature.trim()) {
      setError('Please type your full legal name as your electronic signature.');
      return;
    }

    if (!isValidPhone(formData.phoneNumber)) {
      setError('Please enter a valid phone number.');
      return;
    }

    if (!isValidAddress(formData.currentAddress)) {
      setError('Please enter a valid address.');
      return;
    }

    const payload = {
      ...formData,
      propertyId: parseInt(propertyId),
      landlordId: property.landlord_id,
      income: parseFloat(formData.income || 0),
      numOccupants: parseInt(formData.numOccupants || 1),
      moveInDate: formData.moveInDate || null, // Ensure empty string becomes NULL
      consentVersion,
      consentText
    };

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/tenants/applications`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.token}`,
        },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to submit application');

      trackEvent({
        event_name: 'application_submitted',
        event_category: 'application',
        event_action: 'submit',
        property_id: property.id,
        application_id: data.application?.id
      });

      navigate(`/application/${data.application.id}/documents`); // Redirect to Phase 2
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) {
    return <main className="App-main">Loading application form...</main>;
  }

  if (error) {
    return <main className="App-main"><p className="error-message">{error}</p></main>;
  }

  return (
    <main className="App-main application-form-page">
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
        <h2 style={{ marginBottom: 0 }}>Apply for {property ? `${property.address}, ${property.city}` : '...'}</h2>
        <Link to="/verification" className={`status-pill ${getVerificationBadge().tone}`} title="Learn what landlord verification means">
          {getVerificationBadge().label}
        </Link>
      </div>

      {property?.landlord_ownership_status !== 'approved' && property?.landlord_id_status !== 'verified' && (
        <p className="error-message">
          Applications are disabled until the landlord is verified.
        </p>
      )}


      <form onSubmit={handleSubmit} className="property-form">

        <div className="form-group">
          <label htmlFor="message">Your Message (Optional)</label>
          <textarea id="message" name="message" value={formData.message} onChange={handleChange} rows="4" placeholder="Introduce yourself or ask any questions..."></textarea>
          {renderPrefillNote('message')}
        </div>

        <div className="form-group">
          <label htmlFor="fullName">Full Name:</label>
          <input type="text" id="fullName" name="fullName" value={formData.fullName} onChange={handleChange} required />
          {renderPrefillNote('fullName')}
        </div>
        <div className="form-group">
          <label htmlFor="email">Email:</label>
          <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} onBlur={handleEmailBlur} />
          {renderPrefillNote('email')}
        </div>
        <div className="form-group">
          <label htmlFor="phoneNumber">Phone Number:</label>
          <input type="text" id="phoneNumber" name="phoneNumber" value={formData.phoneNumber} onChange={handleChange} />
          {renderPrefillNote('phoneNumber')}
        </div>
        <div className="form-group">
          <label htmlFor="currentAddress">Current Address:</label>
          <textarea id="currentAddress" name="currentAddress" value={formData.currentAddress} onChange={handleChange} rows="3"></textarea>
          {renderPrefillNote('currentAddress')}
        </div>
        <div className="form-group">
          <label htmlFor="employmentStatus">Employment Status:</label>
          <select id="employmentStatus" name="employmentStatus" value={formData.employmentStatus} onChange={handleChange} required>
            <option value="">Select status...</option>
            <option value="Employed full-time">Employed full-time</option>
            <option value="Employed part-time">Employed part-time</option>
            <option value="Self-employed">Self-employed</option>
            <option value="Student">Student</option>
            <option value="Unemployed">Unemployed</option>
            <option value="Retired">Retired</option>
            <option value="Other">Other</option>
          </select>
        </div>
        <div className="form-group">
          <label htmlFor="currentEmployer">Current Employer (Optional):</label>
          <input type="text" id="currentEmployer" name="currentEmployer" value={formData.currentEmployer} onChange={handleChange} />
        </div>
        <div className="form-group">
          <label htmlFor="income">Monthly Income ($):</label>
          <input type="number" id="income" name="income" value={formData.income} onChange={handleChange} min="0" step="0.01" />
        </div>
        <div className="form-group">
          <label htmlFor="numOccupants">Number of Occupants:</label>
          <input type="number" id="numOccupants" name="numOccupants" value={formData.numOccupants} onChange={handleChange} min="1" />
          {renderPrefillNote('numOccupants')}
        </div>
        <div className="form-group">
          <label htmlFor="petInfo">Pet Information:</label>
          <textarea id="petInfo" name="petInfo" value={formData.petInfo} onChange={handleChange} rows="3"></textarea>
          {renderPrefillNote('petInfo')}
        </div>
        <div className="form-group">
          <label htmlFor="moveInDate">Desired Move-in Date:</label>
          <input type="date" id="moveInDate" name="moveInDate" value={formData.moveInDate} onChange={handleChange} />
          {renderPrefillNote('moveInDate')}
        </div>
        <div className="form-group checkbox-group">
          <input
            type="checkbox"
            id="consentBackgroundCheck"
            name="consentBackgroundCheck"
            checked={formData.consentBackgroundCheck}
            onChange={handleChange}
            required
          />
          <label htmlFor="consentBackgroundCheck">I consent to a background check.</label>
        </div>

        <div className="form-group" style={{ marginTop: '1rem' }}>
          <label style={{ marginBottom: '0.35rem', display: 'block' }}>Applicant Consent & Authorization</label>
          <div className="subtle-text" style={{ background: '#f7f8fb', padding: '0.75rem', borderRadius: '8px' }}>
            {consentText}
          </div>
        </div>

        <div className="form-group checkbox-group">
          <input
            type="checkbox"
            id="consentData"
            name="consentData"
            checked={formData.consentData}
            onChange={handleChange}
            required
          />
          <label htmlFor="consentData">I agree to the Applicant Consent & Authorization for Consumer Report.</label>
        </div>

        <div className="form-group">
          <label htmlFor="consentSignature">Type your full legal name as your electronic signature:</label>
          <input
            type="text"
            id="consentSignature"
            name="consentSignature"
            value={formData.consentSignature}
            onChange={handleChange}
            placeholder="Full legal name"
            required
          />
        </div>

        <div className="form-actions" style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
          <button type="submit" className="primary-button" disabled={!canSubmitApplication || !user}>
            {user ? 'Continue to Document Upload' : 'Create account to continue'}
          </button>
          <Link to={property ? `/properties/${property.id}` : '/properties'} className="secondary-button" style={{ marginTop: '0.5rem' }}>Cancel</Link>
        </div>
      </form>
    </main>
  );
}

export default ApplicationForm;
